import { useContext } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { AuthUser } from "../types/auth";

type UseAuthReturn = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  handleLogin: (credentials: {
    email: string;
    password: string;
  }) => Promise<boolean>;
  handleLogout: () => void;
  login: (email: string, password: string) => Promise<any>;
  logout: () => void;
  navigateToDashboard: () => void;
  refreshUser: () => Promise<void>;
};

const useAuth = (): UseAuthReturn => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  const {
    user,
    login,
    handleLogout: contextLogout,
    loading,
    navigateToDashboard,
  } = context;

  const handleLogin = async (credentials: {
    email: string;
    password: string;
  }) => {
    const result = await login(credentials.email, credentials.password);
    // Navigation is now handled in the login function itself
    return result;
  };

  const handleLogout = () => {
    contextLogout(); // This will handle the navigation in AuthContext
  };

  return {
    user,
    isAuthenticated: !!user,
    loading,
    handleLogin,
    handleLogout,
    login,
    logout: contextLogout,
    navigateToDashboard,
    refreshUser: context?.refreshUser,
  };
};

export default useAuth;
export { useAuth };
